import { Feature, Map } from "ol";
import Point from "ol/geom/Point";
import { fromLonLat } from "ol/proj";
import { useEffect, useRef } from "react";
import { toPOIStyle } from "../../styles";
import { useVectorLayer } from "../utils/useVectorLayer";

// A point of interest as stored in the `<qid>.POI` response
export type POI = { label: string; lon: number; lat: number };

export function parsePOIs(value: string): POI[] {
	if (!value) return [];
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

// Points of interest are kept off the question layer so they are never saved
// as shapes. Each feature carries its position in `pois` as `index`.
export function usePOILayer(map: Map | null, pois: POI[], poiEnabled: boolean,) {
	const hoveredFeatureRef = useRef<Feature | null>(null);
	const { source, layer } = useVectorLayer(map, {
		zIndex: 3,
		style: (feature) => {
            const hovered = feature.get("hovered");

            return toPOIStyle(
                feature.get("label"),
                hovered,
            );
        },
	});

	useEffect(() => {
		source.clear();
		for (let i = 0; i < pois.length; i++) {
			const poi = pois[i];
			const feature = new Feature({
				geometry: new Point(fromLonLat([poi.lon, poi.lat])),
				label: poi.label,
				index: i,
			});
			source.addFeature(feature);
		}
	}, [source, pois]);

	 useEffect(() => {
        if (!map || !poiEnabled) return;

        const handlePointerMove = (evt: any) => {
            const feature = map.forEachFeatureAtPixel(
                evt.pixel,
                (feature, layerCandidate) =>
                    layerCandidate === layer ? feature : null,
            ) as Feature | null;

            const target = map.getTargetElement();
            if (target) target.style.cursor = feature ? "pointer" : "";

            if (hoveredFeatureRef.current === feature) {
                return;
            }

            if (hoveredFeatureRef.current) {
                hoveredFeatureRef.current.set("hovered", false);
                hoveredFeatureRef.current.changed();
            }

            if (feature) {
                feature.set("hovered", true);
                feature.changed();
            }

            hoveredFeatureRef.current = feature;
        };

        map.on("pointermove", handlePointerMove);

        return () => {
            map.un("pointermove", handlePointerMove);
            // The map's target may already be detached on unmount
            const target = map.getTargetElement();
            if (target) target.style.cursor = "";
        };
    }, [map, layer, poiEnabled]);

	return { source, layer };
}
